import { resolveAbsoluteMediaUrl } from '~/utils/mediaUrl'
import { joinStorefrontUrl, storefrontRelativePath, useShopStorefrontRoot } from '~/composables/useShopPublicUrl'

interface SeoOptions {
  title?: string
  description?: string | (() => string | undefined) | import('vue').Ref<string | undefined> | import('vue').ComputedRef<string | undefined>
  image?: string
  url?: string
  type?: 'website' | 'article' | 'product'
  noindex?: boolean
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
}

export function useSeo(options: SeoOptions = {}) {
  const config = useRuntimeConfig()
  const route = useRoute()
  const { site, branding } = useSiteSettings()
  const shopSlug = useShopSlug()
  const storefrontRoot = useShopStorefrontRoot()

  const siteName = computed(() => site.value.name)
  const siteUrl = config.public.siteUrl as string
  const apiOrigin = config.public.apiOrigin as string

  const title = computed(() =>
    options.title
      ? `${options.title} | ${siteName.value}`
      : siteName.value,
  )

  const description = computed(() =>
    toValue(options.description) || branding.value.description,
  )

  const canonicalUrl = computed(() =>
    options.url || joinStorefrontUrl(
      storefrontRoot.value,
      storefrontRelativePath(route.fullPath, shopSlug.value || ''),
    ),
  )

  const ogImage = computed(() =>
    options.image
      ? resolveAbsoluteMediaUrl(options.image, siteUrl, apiOrigin)
      : branding.value.ogImage,
  )

  // Open Graph typed tags don't include "product"; product pages set og:type separately.
  const ogType = options.type === 'article' ? 'article' : 'website'

  useSeoMeta({
    title: () => options.title || siteName.value,
    description,
    ogTitle: title,
    ogDescription: description,
    ogImage,
    ogUrl: canonicalUrl,
    ogType,
    ogSiteName: siteName,
    ogLocale: 'ro_RO',
    twitterCard: 'summary_large_image',
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: ogImage,
    robots: options.noindex ? 'noindex, nofollow' : 'index, follow',
  })

  useHead({
    link: computed(() => [{ rel: 'canonical', href: canonicalUrl.value }]),
    script: options.jsonLd
      ? [{
          type: 'application/ld+json',
          innerHTML: JSON.stringify(
            Array.isArray(options.jsonLd) ? options.jsonLd : options.jsonLd,
          ),
        }]
      : [],
  })

  return {
    title,
    description,
    canonicalUrl,
    formatPrice,
  }
}

function toPriceNumber(value?: number | string | null): number {
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

export function formatPrice(amount: number | string, currencyCode?: string): string {
  const settings = useState<Record<string, string>>('site-settings', () => ({}))
  const currency = (currencyCode || settings.value.shop_currency || 'RON').trim().toUpperCase()
  const safeCurrency = /^[A-Z]{3}$/.test(currency) ? currency : 'RON'

  return new Intl.NumberFormat('ro-RO', {
    style: 'currency',
    currency: safeCurrency,
    minimumFractionDigits: 2,
  }).format(toPriceNumber(amount))
}

export function productJsonLd(
  product: {
    name: string
    description?: string
    slug: string
    price: number | string
    salePrice?: number | string | null
    sku: string
    stock?: number
    ratingAvg?: number | string
    ratingCount?: number
    images?: { url: string }[]
    brand?: { name: string }
  },
  opts: { siteUrl: string, siteName: string, apiOrigin: string, stockManaged?: boolean, reviewsEnabled?: boolean },
) {
  const { siteUrl, siteName, apiOrigin } = opts
  const price = toPriceNumber(product.salePrice ?? product.price)
  const ratingAvg = toPriceNumber(product.ratingAvg)
  const stockManaged = opts.stockManaged !== false
  const inStock = !stockManaged || (product.stock ?? 1) > 0

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description || product.name,
    sku: product.sku,
    image: product.images?.map(img => resolveAbsoluteMediaUrl(img.url, siteUrl, apiOrigin)) || [],
    brand: product.brand ? { '@type': 'Brand', name: product.brand.name } : undefined,
    offers: {
      '@type': 'Offer',
      url: `${siteUrl}/produse/${product.slug}`,
      priceCurrency: 'RON',
      price: price.toFixed(2),
      availability: inStock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'Organization',
        name: siteName,
      },
    },
    aggregateRating: opts.reviewsEnabled !== false && product.ratingCount && product.ratingCount > 0
      ? {
          '@type': 'AggregateRating',
          ratingValue: ratingAvg,
          reviewCount: product.ratingCount,
        }
      : undefined,
  }
}

export function breadcrumbJsonLd(items: { name: string, url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  }
}

export function siteBreadcrumbJsonLd(
  items: { name: string, url: string }[],
  siteUrl: string,
) {
  return breadcrumbJsonLd([
    { name: 'Acasă', url: siteUrl },
    ...items,
  ])
}
